import { DestroyRef, inject, Injectable, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { catchError, interval, of, startWith, switchMap } from 'rxjs';
import { InboxAlarm } from '../models/alert.model';
import { AlertsApiService } from './alerts-api.service';

const POLL_MS = 30_000;
const BROWSER_NOTIFICATION_TAG = 'gemminus-unread-alerts';

const emptyInbox = (): InboxAlarm[] => [];

@Injectable({
  providedIn: 'root',
})
export class NotificationsInboxService {
  private readonly alertsApi = inject(AlertsApiService);
  private readonly destroyRef = inject(DestroyRef);

  readonly unreadCount = signal(0);
  readonly items = signal<InboxAlarm[]>(emptyInbox());
  readonly isLoading = signal(false);

  private hasUnreadBaseline = false;
  private lastKnownUnreadCount = 0;
  private permissionRequested = false;

  constructor() {
    this.requestNotificationPermission();

    interval(POLL_MS)
      .pipe(
        startWith(0),
        switchMap(() =>
          this.alertsApi.unreadCount().pipe(catchError(() => of(null)))
        ),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((result) => {
        if (result && typeof result.unread_count === 'number') {
          this.applyRemoteUnreadCount(result.unread_count);
        }
      });
  }

  requestNotificationPermission(fromUserGesture = false): void {
    const api = this.notificationApi();
    if (!api || api.permission !== 'default') return;
    if (this.permissionRequested && !fromUserGesture) return;
    if (!fromUserGesture && this.userActivationKnownInactive()) return;

    this.permissionRequested = true;
    try {
      void api.requestPermission().catch(() => undefined);
    } catch {
      this.permissionRequested = false;
    }
  }

  loadInbox(): void {
    this.isLoading.set(true);
    this.alertsApi.getInbox().subscribe({
      next: (inbox) => {
        if (!inbox || typeof inbox.unread_count !== 'number') {
          this.isLoading.set(false);
          return;
        }
        this.items.set((inbox.items ?? []).filter((item) => !item.read));
        this.applyRemoteUnreadCount(inbox.unread_count);
        this.isLoading.set(false);
      },
      error: () => {
        this.items.set(emptyInbox());
        this.isLoading.set(false);
      },
    });
  }

  markRead(id: string): void {
    const current = this.items();
    const target = current.find((item) => item._id === id);
    if (!target || target.read) return;

    this.items.set(current.filter((item) => item._id !== id));
    this.setLocalUnreadCount(Math.max(0, this.unreadCount() - 1));

    this.alertsApi.markAlarmRead(id).subscribe({
      error: () => {
        this.items.set(current);
        this.setLocalUnreadCount(this.unreadCount() + 1);
      },
    });
  }

  markAllRead(): void {
    if (this.unreadCount() === 0) return;

    const current = this.items();
    const previousCount = this.unreadCount();
    this.items.set([]);
    this.setLocalUnreadCount(0);

    this.alertsApi.markAllAlarmsRead().subscribe({
      error: () => {
        this.items.set(current);
        this.setLocalUnreadCount(previousCount);
      },
    });
  }

  private applyRemoteUnreadCount(count: number): void {
    const shouldNotify = this.hasUnreadBaseline && count > this.lastKnownUnreadCount;
    this.hasUnreadBaseline = true;
    this.setLocalUnreadCount(count);
    if (shouldNotify) this.notifyUnreadIncrease(count);
  }

  private setLocalUnreadCount(count: number): void {
    this.lastKnownUnreadCount = count;
    this.unreadCount.set(count);
  }

  private notifyUnreadIncrease(count: number): void {
    if (!this.canShowBrowserNotification()) return;

    this.alertsApi
      .getInbox()
      .pipe(
        catchError(() => of(null)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((inbox) => {
        if (!this.canShowBrowserNotification()) return;
        const newest = this.newestUnreadItem(inbox?.items ?? []);
        this.showBrowserNotification(this.notificationTitle(newest), this.notificationBody(newest, count));
      });
  }

  private canShowBrowserNotification(): boolean {
    const api = this.notificationApi();
    if (!api || api.permission !== 'granted') return false;
    if (typeof document === 'undefined') return false;
    if (document.visibilityState === 'visible' && document.hasFocus()) return false;
    return true;
  }

  private showBrowserNotification(title: string, body: string): void {
    const api = this.notificationApi();
    if (!api) return;

    try {
      const notification = new api(title, {
        body,
        lang: 'pt-BR',
        tag: BROWSER_NOTIFICATION_TAG,
        icon: '/favicon.ico',
      });
      notification.onclick = () => {
        window.focus();
        notification.close();
      };
    } catch {
      return;
    }
  }

  private newestUnreadItem(items: InboxAlarm[]): InboxAlarm | null {
    const unread = items.filter((item) => !item.read);
    if (!unread.length) return null;

    return unread.reduce((newest, item) =>
      this.createdAtMs(item) > this.createdAtMs(newest) ? item : newest
    );
  }

  private createdAtMs(item: InboxAlarm): number {
    const value = Date.parse(item.createdAt);
    return Number.isNaN(value) ? 0 : value;
  }

  private notificationTitle(item: InboxAlarm | null): string {
    const name = item?.alert_name?.trim();
    return name || 'Novo alerta';
  }

  private notificationBody(item: InboxAlarm | null, count: number): string {
    const summary = item?.summary?.trim();
    if (summary) return summary;
    if (count === 1) return 'Você tem 1 alerta não lido';
    return `Você tem ${count} alertas não lidos`;
  }

  private notificationApi(): typeof Notification | null {
    if (typeof Notification === 'undefined') return null;
    return Notification;
  }

  private userActivationKnownInactive(): boolean {
    if (typeof navigator === 'undefined' || !('userActivation' in navigator)) return false;
    return !navigator.userActivation.isActive;
  }
}
